import React, { useEffect,useState } from "react";
import { toast } from "react-toastify";
import {
  getCurrencySymbol,
} from "../common/utlity/options";
import BeatLoader from "react-spinners/BeatLoader";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { generateVariantName } from "./varianthelper";
import { 
  loadVariantsForSelection, 
  selectVariant, 
  updateSelectedQuantity,
  confirmVariantSelection,
  resetVariantSelectionState 
} from "../../store/actions/action";
import { useTranslation } from "react-i18next";
import { predefinedSizes, predefinedColors, variantTypes } from "../../helpers/csshelper";
export const VariantSelectionModal = ({ 
  isOpen, 
  onClose, 
  product, 
  onSelectVariant,
  currentIndex,
  businessUuid
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { bussinessUuid: urlBussinessUuid } = useParams();
  
  // Get variant selection state from Redux
  const {
    availableVariants,
    selectedVariant,
    selectedQuantity,
    loading,
    error
  } = useSelector((state) => state.inventory.variantSelection);
  
  const businessData = useSelector((state) => state.bussiness.business);
  const business = Array.isArray(businessData)
    ? businessData.find((b) => b.bussinessUuid === businessUuid || b.bussinessUuid === urlBussinessUuid) || businessData[0]
    : businessData;

  // Load variants when modal opens
  useEffect(() => {
    if (isOpen && product?.productId) {
      dispatch(loadVariantsForSelection(businessUuid, product));
    }
    
    // Reset state when modal closes
    return () => {
      if (!isOpen) {
        dispatch(resetVariantSelectionState());
      }
    };
  }, [isOpen, product, businessUuid, dispatch]);

  // Show error toast if there's an error
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleVariantSelect = (variant) => {
    dispatch(selectVariant(variant));
  };

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value) || 1;
    dispatch(updateSelectedQuantity(value));
  };

  const handleQuantityDecrease = () => {
    dispatch(updateSelectedQuantity(Math.max(1, selectedQuantity - 1)));
  };

  const handleQuantityIncrease = () => {
    if (selectedVariant) {
      dispatch(updateSelectedQuantity(Math.min(selectedVariant.quantity, selectedQuantity + 1)));
    }
  };

  const handleConfirm = () => {
    dispatch(confirmVariantSelection(currentIndex, onSelectVariant, onClose));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-white">
                {t('VARIANT_MODAL.TITLE')}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                {product?.productName} • {loading ? t('VARIANT_MODAL.LOADING') : t('VARIANT_MODAL.VARIANTS_AVAILABLE', { count: availableVariants.length })}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <BeatLoader color="#3b82f6" size={10} />
            </div>
          ) : availableVariants.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-gray-600 dark:text-gray-400 text-lg mb-2">{t('VARIANT_MODAL.NO_VARIANTS_TITLE')}</p>
              <p className="text-gray-500 dark:text-gray-500">{t('VARIANT_MODAL.NO_VARIANTS_DESC')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-800 dark:text-white mb-3">
                  {t('VARIANT_MODAL.AVAILABLE_VARIANTS')}
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  {availableVariants.map((variant, index) => (
                    <div
                      key={variant.variantId || index}
                      className={`border rounded-lg p-3 cursor-pointer transition-all ${
                        selectedVariant?.variantId === variant.variantId
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400"
                          : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      }`}
                      onClick={() => handleVariantSelect(variant)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium text-gray-800 dark:text-white">
                            {variant.variantName || t('VARIANT_MODAL.VARIANT', { number: index + 1 })}
                          </h4>
                          {variant.attributes && variant.attributes.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {variant.attributes.map((attr, attrIdx) => (
                                <span
                                  key={attrIdx}
                                  className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-full"
                                >
                                  {attr.type}: {attr.value}
                                </span>
                              ))}
                            </div>
                          )}
                          {variant.sku && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {t('VARIANT_MODAL.SKU')}: {variant.sku}
                            </p>
                          )}
                          {variant.batchNumber && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {t('VARIANT_MODAL.BATCH')}: {variant.batchNumber}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-gray-800 dark:text-white">
                            {getCurrencySymbol(business?.currency)}{variant.salePrice || product?.salePrice || "0"}
                          </div>
                          <div className={`text-xs ${variant.quantity > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {t('VARIANT_MODAL.STOCK')}: {variant.quantity || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedVariant && (
                <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-800 dark:text-white">
                      {t('VARIANT_MODAL.SELECTED')}: {selectedVariant.variantName}
                    </h4>
                    <div className="font-semibold text-blue-600 dark:text-blue-400">
                      {getCurrencySymbol(business?.currency)}{selectedVariant.salePrice || product?.salePrice || "0"}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('VARIANT_MODAL.QUANTITY')} *
                    </label>
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={handleQuantityDecrease}
                        className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-l-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      >
                        -
                      </button>
                      <input
                        type="number"
                        value={selectedQuantity}
                        onChange={handleQuantityChange}
                        min="1"
                        max={selectedVariant.quantity}
                        className="w-20 px-3 py-2 border-y border-gray-300 dark:border-gray-600 dark:bg-gray-800 text-center text-gray-800 dark:text-white focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={handleQuantityIncrease}
                        className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-r-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      >
                        +
                      </button>
                      <div className="ml-4 text-sm text-gray-600 dark:text-gray-400">
                        {t('VARIANT_MODAL.MAX_AVAILABLE', { quantity: selectedVariant.quantity })}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 dark:text-gray-300">{t('VARIANT_MODAL.TOTAL_PRICE')}:</span>
                      <span className="text-lg font-bold text-green-600 dark:text-green-400">
                        {getCurrencySymbol(business?.currency)}{((selectedVariant.salePrice || 0) * selectedQuantity).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {t('VARIANT_MODAL.CANCEL')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedVariant || availableVariants.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {t('VARIANT_MODAL.ADD_TO_SALE')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
export const VariantModal = ({ 
  isOpen, 
  onClose, 
  product, 
  variantData, 
  setVariantData,
  totalVariantQuantity,
  setTotalVariantQuantity,
  business,
  onSave
}) => {
  const [customModelValue, setCustomModelValue] = useState("");
  const [customAttributeName, setCustomAttributeName] = useState("");
  const [customAttributeValue, setCustomAttributeValue] = useState("");
   const { t } = useTranslation();


  const handleVariantTypeChange = (type) => {
    setVariantData(prev => ({
      ...prev,
      variantType: type
    }));
  };

  const addVariant = () => {
    const orderedQty = product ? Number(product.quantity) || 0 : 0;
    const currentTotal = totalVariantQuantity;
    const remainingQty = Math.max(0, orderedQty - currentTotal);
    
    const newVariant = {
      variantId: `temp_${Date.now()}_${(variantData.variants || []).length}`,
      variantName: "",
      attributes: [],
      quantity: remainingQty > 0 ? remainingQty : 0,
      salePrice: product?.salePrice || "0",
      purchasePrice: product?.purchasePrice || "0",
      batchNumber: product?.batchNumber || ""
    };
    
    setVariantData(prev => ({
      ...prev,
      variants: [...(prev.variants || []), newVariant]
    }));
    
    calculateTotalVariantQuantity([...(variantData.variants || []), newVariant]);
  };

  const removeVariant = (index) => {
    const newVariants = [...(variantData.variants || [])];
    newVariants.splice(index, 1);
    setVariantData(prev => ({
      ...prev,
      variants: newVariants
    }));
    calculateTotalVariantQuantity(newVariants);
  };

  const updateVariantField = (index, field, value) => {
    const newVariants = [...(variantData.variants || [])];
    
    if (field === "attributes") {
      newVariants[index][field] = value;
      newVariants[index].variantName = generateVariantName(value, variantData.variantType);
    } else {
      newVariants[index][field] = value;
      
      if (field === "quantity") {
        calculateTotalVariantQuantity(newVariants);
      }
    }
    
    setVariantData(prev => ({
      ...prev,
      variants: newVariants
    }));
  };

  const calculateTotalVariantQuantity = (variants) => {
    const total = (variants || []).reduce((sum, variant) => sum + (Number(variant.quantity) || 0), 0);
    setTotalVariantQuantity(total);
    return total;
  };



  const addCustomModel = () => {
    if (customModelValue.trim() === "") {
      toast.error("Please enter a model name");
      return;
    }
    
    const currentConfig = variantData.variantConfig || {};
    const updatedConfig = {
      ...currentConfig,
      model: Array.isArray(currentConfig.model) ? [...currentConfig.model] : []
    };
    
    if (!updatedConfig.model.includes(customModelValue)) {
      updatedConfig.model = [...updatedConfig.model, customModelValue];
    }
    
    setVariantData(prev => ({
      ...prev,
      variantConfig: updatedConfig
    }));
    
    setCustomModelValue("");
    toast.success(`Model "${customModelValue}" added to options`);
  };

  const addCustomAttribute = () => {
    if (!customAttributeName.trim() || !customAttributeValue.trim()) {
      toast.error("Please enter both attribute name and value");
      return;
    }
    
    const currentConfig = variantData.variantConfig || {};
    const updatedConfig = {
      ...currentConfig,
      custom: Array.isArray(currentConfig.custom) ? [...currentConfig.custom] : []
    };
    
    const existingCustom = updatedConfig.custom.find(c => c.name === customAttributeName);
    
    if (existingCustom) {
      updatedConfig.custom = updatedConfig.custom.map(c => {
        if (c.name === customAttributeName) {
          return {
            ...c,
            values: [...c.values, customAttributeValue]
          };
        }
        return c;
      });
    } else {
      updatedConfig.custom = [
        ...updatedConfig.custom,
        {
          name: customAttributeName,
          values: [customAttributeValue]
        }
      ];
    }
    
    setVariantData(prev => ({
      ...prev,
      variantConfig: updatedConfig
    }));
    
    setCustomAttributeName("");
    setCustomAttributeValue("");
    toast.success(`Attribute "${customAttributeName}: ${customAttributeValue}" added`);
  };

  if (!isOpen) return null;

  const orderedQty = product ? Number(product.quantity) || 0 : 0;
  const variants = variantData.variants || [];

  return (
    <div className="fixed inset-0 bg-black/60 dark:bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
  <div className="flex items-center justify-between">
    <div>
      <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
        {t("VARIANT_MODEL.CONFIGURE_VARIANTS")}
      </h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
        {product?.productName || 'Product'}
      </p>
    </div>
    <button
      type="button"
      onClick={onClose}
      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
    >
      ✕
    </button>
  </div>
</div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Quantity Summary */}
          <div className="mb-6 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-4">
                <div className="text-sm">
                  <span className="text-gray-600 dark:text-gray-400">{t("MENU.ORDER_QNTITY")}:</span>
                  <span className="ml-2 font-medium text-gray-800 dark:text-gray-100">{orderedQty}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-600 dark:text-gray-400">{t("VARIANT_MODEL.TOTAL_VARIANT_QUANTITY")}:</span>
                  <span className="ml-2 font-medium text-gray-800 dark:text-gray-100">{totalVariantQuantity}</span>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                totalVariantQuantity === orderedQty 
                  ? 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700' 
                  : 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700'
              }`}>
                {totalVariantQuantity === orderedQty ? '✓ Quantities Match' : '✗ Quantities Don\'t Match'}
              </div>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-500 transition-all duration-300"
                style={{ width: `${orderedQty > 0 ? Math.min((totalVariantQuantity / orderedQty) * 100, 100) : 0}%` }}
              />
            </div>
          </div>

          {/* Variant Type Selection */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">{t("VARIANT_MODEL.VARIANT_TYPE")}</h3>
            <div className="flex flex-wrap gap-2">
              {variantTypes.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  className={`px-4 py-2 text-sm rounded-md border transition-colors ${
                    variantData.variantType === type.id 
                      ? 'bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700' 
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700'
                  }`}
                  onClick={() => handleVariantTypeChange(type.id)}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Configuration Options */}
          {(variantData.variantType === "model" || variantData.variantType === "custom") && (
            <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                {variantData.variantType === "model" ? "Model Options" : "Custom Attributes"}
              </h3>
              
              {variantData.variantType === "model" ? (
                <>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                      placeholder="Enter model name"
                      value={customModelValue}
                      onChange={(e) => setCustomModelValue(e.target.value)}
                    />
                    <button
                      type="button"
                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                      onClick={addCustomModel}
                    >
                      {t("VARIANT_MODEL.ADD")}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {variantData.variantConfig?.model?.map((model, idx) => (
                      <span 
                        key={idx} 
                        className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700"
                      >
                        {model}
                      </span>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                    <input
                      type="text"
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                      placeholder="Attribute name"
                      value={customAttributeName}
                      onChange={(e) => setCustomAttributeName(e.target.value)}
                    />
                    <input
                      type="text"
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                      placeholder="Attribute value"
                      value={customAttributeValue}
                      onChange={(e) => setCustomAttributeValue(e.target.value)}
                    />
                    <button
                      type="button"
                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                      onClick={addCustomAttribute}
                    >
                      {t("VARIANT_MODEL.ADD_ATTRIBUTE")}
                    </button>
                  </div>
                  <div className="space-y-3">
                    {variantData.variantConfig?.custom?.map((attr, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-md p-3 dark:border-gray-700">
                        <div className="text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">{attr.name}</div>
                        <div className="flex flex-wrap gap-2">
                          {attr.values.map((value, vIdx) => (
                            <span 
                              key={vIdx} 
                              className="px-3 py-1 bg-purple-50 text-purple-700 text-sm rounded-full border border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700"
                            >
                              {value}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Add Variant Button */}
          <div className="mb-6">
            <button
              type="button"
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 dark:bg-blue-500 dark:hover:bg-blue-600"
              onClick={addVariant}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t("VARIANT_MODEL.ADD_VARIANT")}
            </button>
          </div>
          {/* Variants List */}
          <div className="space-y-4">
            {variants.length > 0 ? (
              variants.map((variant, index) => (
                <div
                  key={variant.variantId || index}
                  className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors dark:border-gray-700 dark:hover:border-gray-500"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-50 rounded-md flex items-center justify-center dark:bg-blue-900">
                        <span className="text-sm font-medium text-blue-700 dark:text-blue-300">{index + 1}</span>
                      </div>
                      {variant.variantName && (
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{variant.variantName}</span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-red-600 transition-colors p-1 dark:text-gray-400 dark:hover:text-red-500"
                      onClick={() => removeVariant(index)}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Size Field */}
                    {(variantData.variantType === "size" || variantData.variantType === "size-color" || variantData.variantType === "size-model") && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2 dark:text-gray-300">
                          {t("FORM.SIZE")} *
                        </label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:focus:ring-blue-400"
                          value={variant.attributes?.find(a => a.type === "size")?.value || ""}
                          onChange={(e) => {
                            const attributes = [...(variant.attributes || [])];
                            const sizeIndex = attributes.findIndex(a => a.type === "size");
                            if (sizeIndex > -1) {
                              attributes[sizeIndex].value = e.target.value;
                            } else {
                              attributes.push({ type: "size", value: e.target.value });
                            }
                            updateVariantField(index, "attributes", attributes);
                          }}
                        >
                          <option value="">Select Size</option>
                          {predefinedSizes.map(size => (
                            <option key={size} value={size}>{size}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Color Field */}
                    {(variantData.variantType === "color" || variantData.variantType === "size-color") && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2 dark:text-gray-300">
                          {t("VARIANT_MODEL.COLOR")} *
                        </label>
                        <div className="flex items-center gap-2">
                          <select
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:focus:ring-blue-400"
                            value={variant.attributes?.find(a => a.type === "color")?.value || ""}
                            onChange={(e) => {
                              const attributes = [...(variant.attributes || [])];
                              const colorIndex = attributes.findIndex(a => a.type === "color");
                              if (colorIndex > -1) {
                                attributes[colorIndex].value = e.target.value;
                              } else {
                                attributes.push({ type: "color", value: e.target.value });
                              }
                              updateVariantField(index, "attributes", attributes);
                            }}
                          >
                            <option value="">Select Color</option>
                            {predefinedColors.map(color => (
                              <option key={color} value={color}>{color}</option>
                            ))}
                          </select>
                          {variant.attributes?.find(a => a.type === "color")?.value && (
                            <div 
                              className="w-8 h-8 rounded-md border border-gray-300 dark:border-gray-600"
                              style={{ 
                                backgroundColor: variant.attributes.find(a => a.type === "color")?.value.toLowerCase(),
                                opacity: variant.attributes.find(a => a.type === "color")?.value.toLowerCase() === 'white' ? 0.9 : 1
                              }}
                            />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Model Field */}
                    {variantData.variantType === "model" && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2 dark:text-gray-300">
                          {t("VARIANT_MODEL.MODEL")} *
                        </label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:focus:ring-blue-400"
                          value={variant.attributes?.find(a => a.type === "model")?.value || ""}
                          onChange={(e) => {
                            const attributes = [...(variant.attributes || [])];
                            const modelIndex = attributes.findIndex(a => a.type === "model");
                            if (modelIndex > -1) {
                              attributes[modelIndex].value = e.target.value;
                            } else {
                              attributes.push({ type: "model", value: e.target.value });
                            }
                            updateVariantField(index, "attributes", attributes);
                          }}
                        >
                          <option value="">Select Model</option>
                          {(variantData.variantConfig?.model || []).map(model => (
                            <option key={model} value={model}>{model}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Custom Attributes */}
                    {variantData.variantType === "custom" && variantData.variantConfig?.custom?.map((attr, attrIndex) => (
                      <div key={attrIndex}>
                        <label className="block text-xs font-medium text-gray-700 mb-2 dark:text-gray-300">
                          {attr.name}
                        </label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:focus:ring-blue-400"
                          value={variant.attributes?.find(a => a.type === attr.name)?.value || ""}
                          onChange={(e) => {
                            const attributes = [...(variant.attributes || [])];
                            const attrIdx = attributes.findIndex(a => a.type === attr.name);
                            if (attrIdx > -1) {
                              attributes[attrIdx].value = e.target.value;
                            } else {
                              attributes.push({ type: attr.name, value: e.target.value });
                            }
                            updateVariantField(index, "attributes", attributes);
                          }}
                        >
                          <option value="">Select {attr.name}</option>
                          {attr.values.map(value => (
                            <option key={value} value={value}>{value}</option>
                          ))}
                        </select>
                      </div>
                    ))}

                    {/* Quantity Field */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2 dark:text-gray-300">
                        {t("SIDER.QUANTITY")} *
                        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                          (Remaining: {orderedQty - totalVariantQuantity + (Number(variant.quantity) || 0)})
                        </span>
                      </label>
                      <input
                        type="number"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:focus:ring-blue-400"
                        min="0"
                        max={orderedQty}
                        value={variant.quantity || 0}
                        onChange={(e) => updateVariantField(index, "quantity", e.target.value)}
                        onWheel={(e) => e.target.blur()}
                      />
                    </div>

                    {/* Sale Price Field */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2 dark:text-gray-300">
                        {t("TABLE.SALE_PRICE")}
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 text-sm dark:text-gray-400">{getCurrencySymbol(business?.currency)}</span>
                        </div>
                        <input
                          type="number"
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:focus:ring-blue-400"
                          value={variant.salePrice || ""}
                          onChange={(e) => updateVariantField(index, "salePrice", e.target.value)}
                          onWheel={(e) => e.target.blur()} 
                          placeholder={product?.salePrice || "0"}
                          step="0.01"
                          min="0"
                        />
                      </div>
                    </div>

                    {/* Batch Number Field */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2 dark:text-gray-300">
                        {t("FORM.BATCH_NUMBER")}
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:focus:ring-blue-400"
                        value={variant.batchNumber || ""}
                        onChange={(e) => updateVariantField(index, "batchNumber", e.target.value)}
                        placeholder={product?.batchNumber || ""}
                      />
                    </div>
                  </div>
                </div>
              ))
            ) : ( <div className="text-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                    <svg
                      className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">
                      {t("VARIANT_MODEL.NO_VARIANTS_ADDED_YET")}
                    </p>
                    <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">
                      {t("VARIANT_MODEL.CLICK_TO_CREATE")}
                    </p>
                  </div>
                )}
                </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center justify-between">
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              onClick={onClose}
            >
              {t("COMMON.CANCEL")}
            </button>
            <div className="flex items-center gap-3">
              <span
                className={`text-xs ${
                  totalVariantQuantity !== orderedQty
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-green-600 dark:text-green-400'
                }`}
              >
                {totalVariantQuantity === orderedQty
                  ? t("VARIANT_MODEL.READY_TO_SAVE")
                  : `Quantity mismatch: ${Math.abs(orderedQty - totalVariantQuantity)} units`}
              </span>
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                onClick={onSave}
                disabled={totalVariantQuantity !== orderedQty}
              >
                {t("COMMON.SAVE")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


